import { useEffect, useId, useRef, useState } from "react";
import { LogOut, History } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useResearchQuota } from "../context/QuotaContext";
import { QUOTA_LOW_REMAINING_THRESHOLD } from "../constants/quota";

function getInitials(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const UserMenu = () => {
  const { user, logout } = useAuth();
  const { quota } = useResearchQuota();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!user) return null;

  const initials = getInitials(user.name, user.email);
  const dailyQuota = quota && !quota.exempt && quota.daily.limit !== 0 ? quota.daily : null;
  const isQuotaLow = dailyQuota ? dailyQuota.remaining <= QUOTA_LOW_REMAINING_THRESHOLD : false;

  return (
    <div ref={containerRef} className="relative font-['DM_Sans']">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        className="relative flex h-11 w-11 items-center justify-center rounded-full bg-[#1a202c] text-sm font-semibold text-white transition-transform duration-150 ease-out hover:scale-[1.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#468BFF]/60 focus-visible:ring-offset-2"
      >
        {initials}
        {isQuotaLow && (
          <span
            aria-label={`Restam ${dailyQuota?.remaining} pesquisas hoje`}
            className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#FE363B] ring-2 ring-white"
          />
        )}
      </button>

      {isOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label="Menu do usuário"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 origin-top-right rounded-2xl border border-gray-200 bg-white shadow-xl transition-all duration-150 ease-out"
        >
          <div className="px-4 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900 truncate">{user.name || user.email}</p>
            {user.name && <p className="text-sm text-gray-500 truncate">{user.email}</p>}
          </div>

          {dailyQuota && (
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs uppercase tracking-[0.06em] text-gray-400 font-medium">Pesquisas hoje</p>
              <p className={`text-sm mt-1 ${isQuotaLow ? "text-[#FE363B] font-medium" : "text-gray-700"}`}>
                {dailyQuota.used} de {dailyQuota.limit}
                {isQuotaLow && ` · restam ${dailyQuota.remaining}`}
              </p>
            </div>
          )}

          <div className="py-2">
            <Link
              to="/history"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
            >
              <History className="h-4 w-4 text-gray-400" aria-hidden="true" />
              Histórico de pesquisas
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                void logout();
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-[#FE363B] hover:bg-[#FE363B]/5 focus:outline-none focus-visible:bg-[#FE363B]/5"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
